import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { FileText, Plus } from 'lucide-react'

import type { ContractDeliveryOrderStatus } from '@/lib/api/contract-delivery-api'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { defineListFilters } from '@/components/common/filters/core'
import { selectFilter } from '@/components/common/filters/select-filter'
import { textFilter } from '@/components/common/filters/text-filter'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { LIST_CARD_TITLE_CLASS, ListCard } from '@/components/common/ListCard'
import { Button } from '@/components/ui/button'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { ContractDeliveryStatusBadge } from '@/components/contract-delivery/contract-delivery-status-badge'
import { useContractDeliveries } from '@/hooks/use-contract-delivery-query'
import { useAuth } from '@/hooks/use-auth'
import { m } from '@/lib/paraglide/messages'

const STATUS_OPTIONS: {
    value: ContractDeliveryOrderStatus
    label: () => string
}[] = [
    { value: 'new', label: m.cdo_status_new },
    { value: 'confirmed', label: m.cdo_status_confirmed },
    { value: 'ready_to_deliver', label: m.cdo_status_ready },
    { value: 'in_delivery', label: m.cdo_status_in_delivery },
    { value: 'delivered', label: m.cdo_status_delivered },
    { value: 'delivery_failed', label: m.cdo_status_failed },
    { value: 'cancelled', label: m.cdo_status_cancelled },
]

/**
 * Bộ lọc khai một lần, suy ra: parser URL, mặc định, badge đếm, chip.
 *
 * Cả hai bộ lọc trước đây nằm trong `useState` nên không vào URL — chia sẻ link
 * hay bấm Back đều mất (R9).
 */
const FILTERS = defineListFilters({
    q: textFilter({
        label: m.cdo_search_label,
        placeholder: m.cdo_search_ph,
    }),
    status: selectFilter<'all' | ContractDeliveryOrderStatus>({
        label: m.cdo_status_filter_label,
        defaultValue: 'all',
        options: () => [
            { value: 'all', label: m.cdo_status_filter_all() },
            ...STATUS_OPTIONS.map((o) => ({
                value: o.value,
                label: o.label(),
            })),
        ],
    }),
})

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/contract-deliveries/',
)({
    component: ContractDeliveriesPage,
    validateSearch: FILTERS.validateSearch,
})

function ContractDeliveriesPage() {
    const { hasPermission } = useAuth()
    const navigate = useNavigate({ from: '/contract-deliveries/' })

    const { values, barProps, page, setPage } = useListFilters({
        def: FILTERS,
        search: Route.useSearch(),
        onSearchChange: (next, opts) =>
            void navigate({
                search: () => next,
                replace: opts?.replace ?? false,
            }),
        columns: 3,
    })

    // Map bộ lọc → tham số API. Để tường minh ở màn: mỗi màn map một kiểu.
    const { data, isLoading } = useContractDeliveries({
        status: values.status === 'all' ? undefined : values.status,
        search: values.q || undefined,
        page,
        per_page: 50,
    })
    const orders = data?.data ?? []
    const meta = data?.meta

    // Rỗng vì chưa có dữ liệu khác hẳn rỗng vì bộ lọc quá hẹp — trường hợp thứ
    // hai phải cho người dùng lối thoát ngay tại chỗ (R10).
    const filtering = barProps.activeCount > 0 || !!values.q
    const emptyState = (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
            <span className="text-muted-foreground">
                {filtering ? m.list_empty_filtered() : m.cdo_empty()}
            </span>
            {filtering ? (
                <ClearFilterButton active onClear={barProps.onClear} />
            ) : null}
        </div>
    )

    return (
        <div className="space-y-4 page-pad">
            <ListPageHeader
                icon={<FileText className="size-5 shrink-0" />}
                title={m.cdo_title()}
                primaryActions={
                    hasPermission('contract_delivery_orders.write') ? (
                        <Button asChild size="sm">
                            <Link
                                to="/contract-deliveries/new"
                                className="w-full"
                            >
                                <Plus className="size-4" />
                                {m.cdo_create_btn()}
                            </Link>
                        </Button>
                    ) : undefined
                }
            />

            <ListFilterBar {...barProps} />

            {/* Mobile: card list */}
            <div className="space-y-2 md:hidden">
                {isLoading ? (
                    <div className="py-8 text-center text-muted-foreground">
                        {m.cdo_loading()}
                    </div>
                ) : orders.length === 0 ? (
                    emptyState
                ) : (
                    orders.map((o) => (
                        <ListCard
                            key={o.id}
                            title={
                                <Link
                                    to="/contract-deliveries/$id"
                                    params={{ id: o.id }}
                                    className={LIST_CARD_TITLE_CLASS}
                                >
                                    {o.order_code}
                                </Link>
                            }
                            subtitle={`${o.recipient_name} · ${o.recipient_phone}`}
                            badge={
                                <ContractDeliveryStatusBadge
                                    status={o.status}
                                />
                            }
                            fields={[
                                {
                                    label: m.cdo_col_address(),
                                    value: o.shipping_address,
                                    full: true,
                                },
                                {
                                    label: m.cdo_col_carrier(),
                                    value: o.shipping_carrier_name || '—',
                                },
                                {
                                    label: m.cdo_col_created_at(),
                                    value: new Date(
                                        o.created_at,
                                    ).toLocaleDateString('vi-VN'),
                                },
                                {
                                    label: m.cdo_col_created_by(),
                                    value: o.created_by_name || '—',
                                    full: true,
                                },
                            ]}
                        />
                    ))
                )}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block rounded-md border overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.cdo_col_code()}</TableHead>
                            <TableHead>{m.cdo_col_recipient()}</TableHead>
                            <TableHead>{m.cdo_col_phone()}</TableHead>
                            <TableHead>{m.cdo_col_address()}</TableHead>
                            <TableHead>{m.cdo_col_carrier()}</TableHead>
                            <TableHead>{m.cdo_col_status()}</TableHead>
                            <TableHead>{m.cdo_col_created_by()}</TableHead>
                            <TableHead>{m.cdo_col_created_at()}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={8}
                                    className="text-center text-muted-foreground py-8"
                                >
                                    {m.cdo_loading()}
                                </TableCell>
                            </TableRow>
                        ) : orders.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8}>{emptyState}</TableCell>
                            </TableRow>
                        ) : (
                            orders.map((o) => (
                                <TableRow key={o.id}>
                                    <TableCell>
                                        <Link
                                            to="/contract-deliveries/$id"
                                            params={{ id: o.id }}
                                            className="font-mono text-xs text-primary hover:underline"
                                        >
                                            {o.order_code}
                                        </Link>
                                    </TableCell>
                                    <TableCell>{o.recipient_name}</TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                        {o.recipient_phone}
                                    </TableCell>
                                    <TableCell className="max-w-[240px] truncate">
                                        {o.shipping_address}
                                    </TableCell>
                                    <TableCell className="text-sm">
                                        {o.shipping_carrier_name || '—'}
                                    </TableCell>
                                    <TableCell>
                                        <ContractDeliveryStatusBadge
                                            status={o.status}
                                        />
                                    </TableCell>
                                    <TableCell className="text-sm">
                                        {o.created_by_name || '—'}
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                        {new Date(
                                            o.created_at,
                                        ).toLocaleDateString('vi-VN')}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination — mirror orders/index.tsx */}
            {meta && meta.total_pages > 1 && (
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>
                        {m.order_total_count({ count: String(meta.total) })}
                    </span>
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page === 1}
                            onClick={() => setPage(page - 1)}
                        >
                            {m.common_pagination_prev()}
                        </Button>
                        <span className="flex items-center px-2">
                            {page} / {meta.total_pages}
                        </span>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page >= meta.total_pages}
                            onClick={() => setPage(page + 1)}
                        >
                            {m.common_pagination_next()}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    )
}
